import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Printer, RefreshCw } from "lucide-react";
import { devError } from "@/lib/logger";

const STATUS_OPTS = ["All", "Pending", "Cleared", "Bounced", "Cancelled"];
const STATUS_STYLE = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  Cleared: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Bounced: "bg-red-100 text-red-800 border-red-200",
  Cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

function StatCard({ label, count, amount, accent }) {
  return (
    <div className="border border-gray-200 rounded bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${accent || "text-gray-900"}`}>{count}</div>
      <div className="text-[11px] text-gray-500 font-mono">₹ {formatINR(amount)}</div>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [statusFilter, setStatusFilter] = useState("All");
  const [data, setData] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/cheques`, {
      params: {
        ...params,
        ...(statusFilter !== "All" ? { status: statusFilter } : {}),
      },
    })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("Cheque fetch failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, statusFilter, refreshTick]);

  const refetch = () => setRefreshTick((t) => t + 1);

  const vouchers = useMemo(() => data?.vouchers || [], [data]);

  const updateStatus = async (vid, newStatus) => {
    try {
      await api.patch(`/vouchers/${vid}/cheque-status`, {
        status: newStatus,
        cleared_on: newStatus === "Cleared" ? new Date().toISOString().slice(0, 10) : "",
      });
      toast.success(`Cheque marked as ${newStatus}`);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <>
      <PageHeader
        title="Cheque Register"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/banking"><Button variant="outline" className="h-8" data-testid="btn-back-banking"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button></Link>
            <Button variant="outline" onClick={refetch} className="h-8" data-testid="btn-refresh"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        }
      />
      <div className="p-6 space-y-4" data-testid="cheque-register-page">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="select-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTS.map((s) => <SelectItem key={s} value={s} data-testid={`opt-status-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        </div>

        {/* Stat cards */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cheque-stats">
            <StatCard label="Pending" count={data.by_status.Pending || 0} amount={data.total_amount.Pending || 0} accent="text-amber-700" />
            <StatCard label="Cleared" count={data.by_status.Cleared || 0} amount={data.total_amount.Cleared || 0} accent="text-emerald-700" />
            <StatCard label="Bounced" count={data.by_status.Bounced || 0} amount={data.total_amount.Bounced || 0} accent="text-red-700" />
            <StatCard label="Cancelled" count={data.by_status.Cancelled || 0} amount={data.total_amount.Cancelled || 0} />
          </div>
        )}

        {/* Table */}
        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Cheque No.</th>
                <th>Bank</th>
                <th>Voucher</th>
                <th>Payee</th>
                <th className="text-right">Amount</th>
                <th>Status</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="cheque-rows">
              {vouchers.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-xs text-gray-400 py-6">No cheques in this period. Add a Payment voucher and fill in Cheque Details.</td></tr>
              ) : vouchers.map((v) => {
                const cd = v.cheque_details || {};
                const status = cd.status || "Pending";
                return (
                  <tr key={v.id} data-testid={`cheque-row-${v.id}`}>
                    <td className="font-mono text-xs">{v.date}</td>
                    <td className="font-mono text-xs">{cd.cheque_no || "—"}</td>
                    <td className="text-xs">{cd.bank_name || "—"}</td>
                    <td className="font-mono text-xs">{v.voucher_no} ({v.voucher_type})</td>
                    <td className="text-xs">{cd.payee || v.party_ledger_name || "—"}</td>
                    <td className="tx-num font-semibold">{formatINR(v.total)}</td>
                    <td>
                      <span className={`text-[11px] border rounded px-2 py-0.5 ${STATUS_STYLE[status] || ""}`}>{status}</span>
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Select value={status} onValueChange={(s) => updateStatus(v.id, s)}>
                          <SelectTrigger className="h-7 w-28 text-xs" data-testid={`select-status-${v.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["Pending", "Cleared", "Bounced", "Cancelled"].map((s) => (
                              <SelectItem key={s} value={s} data-testid={`opt-status-${v.id}-${s}`}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Link to={`/banking/cheques/${v.id}/print`}>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`btn-print-${v.id}`}>
                            <Printer className="w-3 h-3 mr-1" /> Print
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-gray-400">
          Tip: When creating a Payment / Receipt voucher, fill in the Cheque No., Bank Name and Payee under the optional Cheque Details section to add it here.
        </div>
      </div>
    </>
  );
}

export default function ChequeRegister() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
